import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

export default function NavBar({ isAuthenticated, isGuest, isAnonymous, onLogout, onLogin, username }) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const mobileMenuRef = useRef(null);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Don't close if clicking on the hamburger button
      if (event.target.closest('.mobile-menu-button')) {
        return;
      }
      
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) {
        setIsMobileMenuOpen(false);
      }
    };

    if (isMobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobileMenuOpen]);


  return (
    <>
      <style>
        {`
          .nav-link:hover {
            background-color: rgba(255, 255, 255, 0.1);
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          }
          .nav-link::before {
            content: '';
            position: absolute;
            top: 0;
            left: -100%;
            width: 100%;
            height: 100%;
            background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
            transition: left 0.5s;
          }
          .nav-link:hover::before {
            left: 100%;
          }
          .nav-button:hover {
            background-color: white;
            color: #3b82f6;
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          }
          /* Base logo styles */
          .nav-logo {
            transform: translateY(4px); /* Base position - moved down slightly */
            transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
          }
          
          /* Desktop hover effect - only applies to devices that support hover */
          @media (hover: hover) and (pointer: fine) {
            .nav-logo:hover {
              transform: translateY(1px); /* 4px - 3px = 1px (more noticeable movement) */
            }
          }
          
          /* Touch devices - no hover effects, but allow click animations */
          @media (hover: none) and (pointer: coarse) {
            .nav-logo:active {
              transform: translateY(1px); /* 4px - 3px = 1px (more noticeable movement) */
            }
          }
          
          /* Mobile screens - ensure consistent behavior for touch devices */
          @media (max-width: 768px) and (hover: none) and (pointer: coarse) {
            .nav-logo:active {
              transform: translateY(1px); /* 4px - 3px = 1px (more noticeable movement) */
            }
          }
          
          /* Mobile menu styles */
          .mobile-menu-button {
            display: none;
            background: none;
            border: none;
            cursor: pointer;
            padding: 0.5rem;
            flex-direction: column;
            gap: 4px;
            transition: all 0.3s ease;
          }
          
          .hamburger-line {
            width: 25px;
            height: 3px;
            background-color: white;
            transition: all 0.3s ease;
            border-radius: 2px;
          }
          
          .mobile-menu-button.open .hamburger-line:nth-child(1) {
            transform: rotate(45deg) translate(6px, 6px);
          }
          
          .mobile-menu-button.open .hamburger-line:nth-child(2) {
            opacity: 0;
          }
          
          .mobile-menu-button.open .hamburger-line:nth-child(3) {
            transform: rotate(-45deg) translate(6px, -6px);
          }
          
          .mobile-menu {
            display: none !important;
            position: absolute;
            top: 100%;
            right: 0;
            width: 50%;
            max-width: 300px;
            background: linear-gradient(135deg, rgba(59, 130, 246, 0.9), rgba(29, 78, 216, 0.9));
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
            z-index: 1000;
            padding: 1rem;
            flex-direction: column;
            gap: 1rem;
            animation: slideDown 0.3s ease-out;
            border-radius: 0 0 0 12px;
          }
          
          .mobile-menu.open {
            display: flex !important;
          }
          
          .mobile-username {
            display: none;
            color: white;
            font-weight: 600;
            font-size: 1.1rem;
            padding: 0.5rem 0;
            border-bottom: 1px solid rgba(255, 255, 255, 0.2);
            margin-bottom: 0.5rem;
          }
          
          .mobile-nav-link {
            display: block;
            color: white;
            text-decoration: none;
            padding: 0.75rem 1rem;
            border-radius: 8px;
            transition: all 0.3s ease;
            font-weight: 500;
            background: rgba(255, 255, 255, 0.1);
            margin-bottom: 0.5rem;
            font-size: 0.9rem;
          }
          
          .mobile-nav-link:hover {
            background: rgba(255, 255, 255, 0.2);
            transform: translateX(5px);
          }
          
          .mobile-nav-button {
            display: block;
            background: white;
            color: #3b82f6;
            border: none;
            padding: 0.75rem 1.5rem;
            border-radius: 8px;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.3s ease;
            width: 100%;
            margin-top: 0.5rem;
            font-size: 0.9rem;
          }
          
          .mobile-nav-button:hover {
            background: #f0f4ff;
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          }
          
          @keyframes slideDown {
            from {
              opacity: 0;
              transform: translateY(-10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          /* Responsive styles */
          @media (max-width: 768px) {
            .navbar {
              padding: 0 1rem !important;
              position: relative !important;
            }
            .navbar-left {
              gap: 0.5rem !important;
            }
            .navbar-right {
              gap: 0.5rem !important;
            }
            .nav-link {
              display: none !important;
            }
            .nav-button {
              display: none !important;
            }
            .mobile-menu-button {
              display: flex !important;
            }
            .mobile-menu {
              display: none !important;
            }
            .mobile-menu.open {
              display: flex !important;
            }
            .nav-logo {
              width: 100px !important;
              height: 35px !important;
            }
            /* Hide username from navbar on mobile */
            .navbar .username {
              display: block !important;
              font-size: 0.8rem !important;
              color: white !important;
              font-weight: 500 !important;
            }
            .navbar .guestLabel {
              display: block !important;
              font-size: 0.8rem !important;
              color: white !important;
              font-weight: 500 !important;
            }
          }
          
          @media (max-width: 480px) {
            .navbar {
              padding: 0 0.5rem !important;
            }
            .navbar-left {
              gap: 0.25rem !important;
            }
            .navbar-right {
              gap: 0.25rem !important;
            }
            .nav-logo {
              width: 110px !important;
              height: 50px !important;
            }
            .mobile-menu {
              padding: 0.8rem !important;
            }
          }
        `}
      </style>
    <nav style={styles.navbar} className="navbar">
      <div style={styles.left} className="navbar-left">
        {/* <Link to="/" style={styles.link}>Home</Link> */}
        <Link to="/" style={styles.link}>
          <img src="/navbar_logo.png" alt="MoodTune Logo" width="48" height="48" style={styles.logo} className="nav-logo" />
        </Link>
        {(isAuthenticated || isAnonymous) && (
          <>
            <Link to="/search" style={styles.link} className="nav-link">Search</Link>
            <Link to="/history" style={styles.link} className="nav-link">History</Link>
          </>
        )}
        <Link to="/about" style={styles.link} className="nav-link">About</Link>
        <Link to="/contact" style={styles.link} className="nav-link">Contact</Link>
      </div>

      <div style={styles.right} className="navbar-right">
        {(isAuthenticated || isAnonymous) ? (
          <div style={styles.authGroup}>
            {isGuest || isAnonymous ? (
              <span style={styles.guestLabel} className="guestLabel">Guest Mode</span>
            ) : (
              username && <span style={styles.username} className="username">Hi, {username}</span>
            )}
            <button style={styles.button} className="nav-button" onClick={isGuest || isAnonymous ? onLogin : onLogout}>
              {isGuest || isAnonymous ? 'Login' : 'Logout'}
            </button>
          </div>
        ) : (
          <button style={styles.button} className="nav-button" onClick={onLogin}>
            Login
          </button>
        )}
        
        {/* Mobile Menu Button */}
        <button 
          className={`mobile-menu-button ${isMobileMenuOpen ? 'open' : ''}`}
          onClick={toggleMobileMenu}
          aria-label="Toggle mobile menu"
        >
          <div className="hamburger-line"></div>
          <div className="hamburger-line"></div>
          <div className="hamburger-line"></div>
        </button>
      </div>
      
      {/* Mobile Menu */}
      <div ref={mobileMenuRef} className={`mobile-menu ${isMobileMenuOpen ? 'open' : ''}`}>
        {/* Username at top of mobile menu */}
        
        {/* Navigation Links */}
        {(isAuthenticated || isAnonymous) && (
          <>
            <Link to="/search" className="mobile-nav-link" onClick={() => setIsMobileMenuOpen(false)}>
              Search
            </Link>
            <Link to="/history" className="mobile-nav-link" onClick={() => setIsMobileMenuOpen(false)}>
              History
            </Link>
          </>
        )}
        <Link to="/about" className="mobile-nav-link" onClick={() => setIsMobileMenuOpen(false)}>
          About
        </Link>
        <Link to="/contact" className="mobile-nav-link" onClick={() => setIsMobileMenuOpen(false)}>
          Contact
        </Link>
        
        {/* Auth Button */}
        {(isAuthenticated || isAnonymous) ? (
          <button 
            className="mobile-nav-button" 
            onClick={() => {
              if (isGuest || isAnonymous) {
                onLogin();
              } else {
                onLogout();
              }
              setIsMobileMenuOpen(false);
            }}
          >
            {isGuest || isAnonymous ? 'Login' : 'Logout'}
          </button>
        ) : (
          <button 
            className="mobile-nav-button" 
            onClick={() => {
              onLogin();
              setIsMobileMenuOpen(false);
            }}
          >
            Log In
          </button>
        )}
      </div>
    </nav>
    </>
  );
}

const styles = {
  navbar: {
    backgroundColor: '#3b82f6',
    color: 'white',
    padding: '0.8rem 2rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontFamily: '"Segoe UI", sans-serif',
    fontSize: '1.1rem',
    margin: 0,
    borderBottom: '2px solid #3b82f6',
    height: '60px', // Fixed height - no expansion allowed
    boxSizing: 'border-box', // Includes padding in height calculation
  },
  left: {
    display: 'flex',
    alignItems: 'center',
    gap: '1.5rem', // Wider spacing between navigation links
    justifyContent: 'flex-start', // Align to the left
  },
  right: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
  },
  link: {
    color: 'white',
    textDecoration: 'none',
    fontWeight: '500',
    padding: '0.5rem 1rem', // Tighter padding for more compact buttons
    borderRadius: '8px',
    transition: 'all 0.3s ease',
    position: 'relative',
    overflow: 'hidden',
  },
  authGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
  },
  guestLabel: {
    fontSize: '0.95rem',
    fontStyle: 'italic',
    color: '#e5e7eb',
  },
  button: {
    padding: '0.5rem 1.1rem',
    fontSize: '1rem',
    color: 'white',
    backgroundColor: 'transparent',
    border: '2px solid white',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: '600',
    transition: 'all 0.3s ease',
  },
  logo: {
    cursor: 'pointer',
    height: '50px',
    width: '150px',
    objectFit: 'contain',
    objectPosition: 'center',
    position: 'relative',
    zIndex: 10,
    filter: 'none',
  }
};